import { motion } from "framer-motion";
import { Truck, Clock, Package, MapPin } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

export default function ShippingPolicyPage() {
  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <Truck className="w-20 h-20 mx-auto mb-6" />
            <h1 className="text-6xl font-black mb-6">Shipping & Delivery Policy</h1>
            <p className="text-2xl text-blue-100 max-w-3xl mx-auto">
              Fast, reliable delivery across UAE & Saudi Arabia
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="space-y-8"
        >
          {/* Shipping Information */}
          <Card className="border-2 border-blue-200 shadow-xl">
            <CardContent className="p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Package className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-3xl font-black text-gray-900 mb-4">Shipping Information</h2>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    We offer <strong>FREE SHIPPING</strong> on all LCTX Tire Changer orders across UAE & Saudi Arabia. Your order will be carefully packaged and shipped within 24-48 hours of order confirmation.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Delivery Times */}
          <Card className="border-2 border-green-200 shadow-xl">
            <CardContent className="p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-8 h-8 text-green-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-3xl font-black text-gray-900 mb-4">Delivery Times</h2>
                  <div className="space-y-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-bold text-gray-900 mb-2">📦 Processing Time</h3>
                      <p className="text-gray-700">Orders are processed and shipped within <strong>24-48 hours</strong> (business days) after payment confirmation.</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-bold text-gray-900 mb-2">🚚 Transit Time</h3>
                      <ul className="space-y-2 text-gray-700">
                        <li>• <strong>Major Cities</strong> (Dubai, Abu Dhabi, Riyadh, Jeddah, Dammam): 3-5 business days</li>
                        <li>• <strong>Regional Areas</strong>: 5-7 business days</li>
                        <li>• <strong>Remote Areas</strong>: 7-10 business days</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Shipping Areas */}
          <Card className="border-2 border-purple-200 shadow-xl">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-8 h-8 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-3xl font-black text-gray-900 mb-4">Shipping Coverage</h2>
                  <p className="text-lg text-gray-700 mb-4">We ship to all Emirates and Provinces in UAE & Saudi Arabia:</p>
                  <div className="grid md:grid-cols-2 gap-3">
                    {[
                      "Abu Dhabi",
                      "Dubai",
                      "Sharjah",
                      "Ajman",
                      "Umm Al Quwain",
                      "Ras Al Khaimah",
                      "Fujairah (UAE)",
                      "Riyadh Province",
                      "Makkah Province",
                      "Madinah Province",
                      "Eastern Province (Saudi Arabia)"
                    ].map((state, index) => (
                      <div key={index} className="flex items-center gap-2 bg-purple-50 rounded-lg p-3">
                        <span className="text-purple-600 font-bold">✓</span>
                        <span className="text-gray-700">{state}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tracking */}
          <Card className="border-2 border-orange-200 shadow-xl">
            <CardContent className="p-8">
              <h2 className="text-3xl font-black text-gray-900 mb-4">📦 Order Tracking</h2>
              <p className="text-lg text-gray-700 mb-4">
                Once your order ships, you will receive:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="text-orange-600 font-bold text-xl">1.</span>
                  <span><strong>Shipping Confirmation Email</strong> with tracking number</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-orange-600 font-bold text-xl">2.</span>
                  <span><strong>Tracking Link</strong> to monitor your delivery in real-time</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-orange-600 font-bold text-xl">3.</span>
                  <span><strong>Delivery Updates</strong> via email and SMS</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Important Notes */}
          <Card className="bg-yellow-50 border-2 border-yellow-300 shadow-xl">
            <CardContent className="p-8">
              <h2 className="text-3xl font-black text-gray-900 mb-4">⚠️ Important Notes</h2>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="text-yellow-600 font-bold">•</span>
                  <span>Delivery times are estimates and may vary due to courier workload or unforeseen circumstances</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-yellow-600 font-bold">•</span>
                  <span>Please ensure someone is available to receive the delivery (signature may be required)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-yellow-600 font-bold">•</span>
                  <span>We currently ship within UAE and Saudi Arabia only. International shipping is not available</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-yellow-600 font-bold">•</span>
                  <span>For any shipping inquiries, contact us at hkjenholtd@gmail.com</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}